import React from 'react';
import { AlertTriangle, Download, Loader2 } from 'lucide-react';
import { Button } from '@roger/ui-components';
import type { ImportedAllergyProfileResponse } from '../../services/api';
import {
  CARRY_FORWARD_NKA_LABEL,
  IMPORTED_NKA_CARRIED_COPY,
  IMPORTED_NKA_COPY,
  OPEN_IMPORTER_TOOL_LABEL,
  convertedCountCopy,
} from './importedChartLineCopy';
import ImportedConversionsNote from './ImportedConversionsNote';

/**
 * What the import knows about this patient's allergies, above the list.
 *
 * Two separate things live here, and they are separate on purpose.
 *
 * **The NKA assertion.** Kinnser saying "no known allergies" is a statement
 * somebody made, and an empty allergy list is the absence of one. They look
 * identical in a row count, so the assertion gets its own visible state and its
 * own verb. Carrying it forward writes `patients.no_known_allergies` — the one
 * place Roger records the fact — and afterwards this says so, rather than
 * asking the next clinician the same question.
 *
 * **Outstanding lines.** The button into the importer tool, shown only while
 * something still waits on a ruling.
 *
 * The carry action itself lives with the parent: asserting NKA is guarded
 * (coded allergies, unreconciled imported lines — 2026-08-28 ruling), and the
 * guard must be one piece of logic no matter which entrance asked.
 */
const ImportedAllergiesNotice: React.FC<{
  profile: ImportedAllergyProfileResponse | null | undefined;
  outstandingCount: number;
  /** Lines the import coded on a certain match (EMR-2415). */
  convertedCount?: number;
  canReconcile: boolean;
  onOpenImporter?: () => void;
  /** Routes through the parent's NKA guard; absent hides the button. */
  onCarryForward?: () => void;
  isCarrying?: boolean;
}> = ({
  profile,
  outstandingCount,
  convertedCount = 0,
  canReconcile,
  onOpenImporter,
  onCarryForward,
  isCarrying = false,
}) => {
  if (!profile || !profile.profile_imported) {
    return null;
  }

  const showNka = profile.no_known_allergies;
  const nkaCarried = Boolean(profile.acted_at);
  // Nothing raw left: only the quiet way back to the conversions, if any.
  const conversionsNote =
    outstandingCount === 0 ? (
      <ImportedConversionsNote
        convertedCount={convertedCount}
        kind='allergy'
        onOpenImporter={onOpenImporter}
      />
    ) : null;
  if (!showNka && outstandingCount === 0) {
    return conversionsNote;
  }

  return (
    <>
      <div
        className='mb-4 rounded-md border border-amber-200 bg-amber-50 p-3'
        data-testid='imported-allergies-notice'
      >
        <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between'>
          <div className='flex gap-2'>
            <AlertTriangle className='h-4 w-4 shrink-0 text-amber-600 mt-0.5' />
            <div className='space-y-1'>
              {showNka && (
                <p
                  className='text-sm text-amber-900'
                  data-testid='imported-nka'
                >
                  {IMPORTED_NKA_COPY}
                </p>
              )}
              {showNka && nkaCarried && (
                <p
                  className='text-xs text-amber-800'
                  data-testid='imported-nka-carried'
                >
                  {IMPORTED_NKA_CARRIED_COPY}
                </p>
              )}
              {outstandingCount > 0 && (
                <p className='text-xs text-amber-800'>
                  {outstandingCount} imported allergy record
                  {outstandingCount === 1 ? '' : 's'} to review.
                  {convertedCount > 0 &&
                    ` ${convertedCountCopy(convertedCount, 'allergy')}`}
                </p>
              )}
            </div>
          </div>
          <div className='flex flex-wrap gap-2 self-start'>
            {showNka && !nkaCarried && canReconcile && onCarryForward && (
              <Button
                size='sm'
                variant='outline'
                onClick={onCarryForward}
                disabled={isCarrying}
                data-testid='carry-forward-nka'
              >
                {isCarrying && (
                  <Loader2 className='h-3.5 w-3.5 mr-1.5 animate-spin' />
                )}
                {CARRY_FORWARD_NKA_LABEL}
              </Button>
            )}
            {outstandingCount > 0 && onOpenImporter && (
              <Button
                size='sm'
                variant='outline'
                onClick={onOpenImporter}
                data-testid='open-importer-tool'
              >
                <Download className='h-3.5 w-3.5 mr-1.5' />
                {OPEN_IMPORTER_TOOL_LABEL}
              </Button>
            )}
          </div>
        </div>
      </div>
      {conversionsNote}
    </>
  );
};

export default ImportedAllergiesNotice;
